import { shallowRef, watch, type CSSProperties, type Ref } from "vue"

export function useComboboxPosition(
  anchor: Readonly<Ref<HTMLElement | null>>,
  options: Readonly<Ref<HTMLElement | undefined>>,
  enabled: Readonly<Ref<boolean>>
) {
  const optionsStyle = shallowRef<CSSProperties>({})

  function updatePosition() {
    if (!enabled.value || !anchor.value || !options.value) return

    const rect = anchor.value.getBoundingClientRect()
    const viewport = window.visualViewport
    const viewportTop = viewport?.offsetTop ?? 0
    const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight)
    const gap = 4
    const padding = 8
    const spaceAbove = Math.max(0, rect.top - viewportTop - gap - padding)
    const spaceBelow = Math.max(0, viewportBottom - rect.bottom - gap - padding)
    const maxHeight = 16 * parseFloat(getComputedStyle(document.documentElement).fontSize)
    const desiredHeight = Math.min(options.value.scrollHeight, maxHeight)
    const openAbove = spaceBelow < desiredHeight && spaceAbove > spaceBelow

    optionsStyle.value = {
      top: openAbove ? "auto" : "100%",
      bottom: openAbove ? "100%" : "auto",
      marginTop: openAbove ? "0" : `${gap}px`,
      marginBottom: openAbove ? `${gap}px` : "0",
      maxHeight: `${Math.min(maxHeight, openAbove ? spaceAbove : spaceBelow)}px`
    }
  }

  watch(
    [enabled, anchor, options],
    ([isEnabled, anchorElement, optionsElement], _, onCleanup) => {
      optionsStyle.value = {}
      if (!isEnabled || !anchorElement || !optionsElement) return

      updatePosition()
      const observer = new ResizeObserver(updatePosition)
      observer.observe(anchorElement)
      observer.observe(optionsElement)
      window.addEventListener("resize", updatePosition)
      window.addEventListener("scroll", updatePosition, true)
      const viewport = window.visualViewport
      viewport?.addEventListener("resize", updatePosition)
      viewport?.addEventListener("scroll", updatePosition)

      onCleanup(() => {
        observer.disconnect()
        window.removeEventListener("resize", updatePosition)
        window.removeEventListener("scroll", updatePosition, true)
        viewport?.removeEventListener("resize", updatePosition)
        viewport?.removeEventListener("scroll", updatePosition)
      })
    },
    { flush: "post" }
  )

  return { optionsStyle, updatePosition }
}
